import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import type { ActivityPart, TokenUsageSummary } from '../../types/chat';

interface TokenUsageSummaryCardProps {
  part: ActivityPart;
}

function formatTokenCount(count: number): string {
  if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
  return String(count);
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function UsageRow({ label, usage }: { label: string; usage: TokenUsageSummary }) {
  return (
    <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
      <span className="font-medium text-gray-600 dark:text-gray-300 min-w-[80px]">{label}</span>
      {usage.durationMs > 0 && <span>{formatDuration(usage.durationMs)}</span>}
      <span>
        {formatTokenCount(usage.inputTokens)} / {formatTokenCount(usage.outputTokens)} tokens
      </span>
    </div>
  );
}

/**
 * Compact card for persistent agent-complete activity events.
 *
 * Shows agent name, duration, and token usage breakdown.
 * When children are present, shows a collapsible per-child breakdown.
 */
export function TokenUsageSummaryCard({ part }: TokenUsageSummaryCardProps) {
  const { data } = part;
  const usage = data.usage;
  const [childrenExpanded, setChildrenExpanded] = useState(false);
  const hasChildren = usage?.children && Object.keys(usage.children).length > 0;
  const depth = data.ancestry.length;
  const nestingMargin = depth > 1 ? `${(depth - 1) * 12}px` : '0px';

  return (
    <div
      className="py-1.5 px-3 text-xs rounded-lg bg-emerald-50 border border-emerald-100"
      style={{ marginLeft: nestingMargin }}
      data-testid="token-usage-summary"
      data-source={data.source}
    >
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        <span className="font-medium text-emerald-700 dark:text-emerald-400">{data.source}</span>
        {usage ? (
          <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
            {usage.durationMs > 0 && (
              <span>{formatDuration(usage.durationMs)}</span>
            )}
            <span title="Input / Output tokens">
              {formatTokenCount(usage.inputTokens)} / {formatTokenCount(usage.outputTokens)} tokens
            </span>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-500">completed</span>
        )}
        {hasChildren && (
          <button
            onClick={() => setChildrenExpanded(!childrenExpanded)}
            className="flex items-center gap-0.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-auto"
          >
            {childrenExpanded ? (
              <ChevronDown className="w-3 h-3" />
            ) : (
              <ChevronRight className="w-3 h-3" />
            )}
            <span>{Object.keys(usage!.children!).length} agents</span>
          </button>
        )}
      </div>
      {childrenExpanded && hasChildren && (
        <div className="mt-1.5 pl-6 space-y-0.5 border-l-2 border-emerald-200 ml-1">
          {Object.entries(usage!.children!).map(([slug, childUsage]) => (
            <UsageRow key={slug} label={slug} usage={childUsage} />
          ))}
        </div>
      )}
    </div>
  );
}
